import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import { useCreateMove } from '@/hooks/useCreateMove'

interface CreateMoveForm {
  name: string
  description: string
  url: string
  thumbnailUrl: string
  durationSeconds: string // Store as string for input, convert to number on submit
}

interface FormErrors {
  name?: string
  description?: string
  url?: string
  thumbnailUrl?: string
  durationSeconds?: string
}

export function CreateMovePage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const createMoveMutation = useCreateMove()

  // Scroll to top on mount
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  const [formData, setFormData] = useState<CreateMoveForm>({
    name: '',
    description: '',
    url: '',
    thumbnailUrl: '',
    durationSeconds: '',
  })
  const [errors, setErrors] = useState<FormErrors>({})

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    // Name is required (1-255 chars)
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    } else if (formData.name.length > 255) {
      newErrors.name = 'Name must be 255 characters or less'
    }

    // Description is required
    if (!formData.description.trim()) {
      newErrors.description = 'Description is required'
    }

    // URL validation (optional but must be valid URL if provided)
    if (formData.url && formData.url.trim()) {
      try {
        new URL(formData.url)
        if (formData.url.length > 500) {
          newErrors.url = 'URL must be 500 characters or less'
        }
      } catch {
        newErrors.url = 'Please enter a valid URL'
      }
    }

    // Thumbnail URL validation (optional but must be valid URL if provided)
    if (formData.thumbnailUrl && formData.thumbnailUrl.trim()) {
      try {
        new URL(formData.thumbnailUrl)
        if (formData.thumbnailUrl.length > 500) {
          newErrors.thumbnailUrl =
            'Thumbnail URL must be 500 characters or less'
        }
      } catch {
        newErrors.thumbnailUrl = 'Please enter a valid URL'
      }
    }

    // Duration validation (optional but must be positive number if provided)
    if (formData.durationSeconds && formData.durationSeconds.trim()) {
      const duration = Number(formData.durationSeconds)
      if (isNaN(duration) || duration < 0) {
        newErrors.durationSeconds = 'Duration must be a positive number'
      } else if (duration > 86400) {
        newErrors.durationSeconds =
          'Duration cannot exceed 24 hours (86400 seconds)'
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e?: React.FormEvent) => {
    e?.preventDefault()

    if (!validateForm()) {
      return
    }

    // Prepare data for API
    const moveData = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      url: formData.url.trim() || undefined,
      thumbnailUrl: formData.thumbnailUrl.trim() || undefined,
      durationSeconds: formData.durationSeconds
        ? Number(formData.durationSeconds)
        : undefined,
    }

    // Create move via API
    createMoveMutation.mutate(moveData, {
      onSuccess: newMove => {
        toast({
          title: 'Success!',
          description: `"${newMove.name}" created successfully`,
        })
        // Navigate to the new move's detail page
        navigate(`/moves/${newMove.id}`)
      },
      onError: error => {
        toast({
          title: 'Failed to create move',
          description:
            error instanceof Error ? error.message : 'An error occurred',
          variant: 'destructive',
        })
      },
    })
  }

  return (
    <div className="flex min-h-dvh flex-col bg-[#F9F9F9]">
      <main className="mx-auto w-full max-w-6xl space-y-3 px-4 py-3">
        <Card className="p-6">
          {/* Header and Action Buttons */}
          <div className="mb-6 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-black">
              Move Information
            </h2>
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/moves')}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={
                  !formData.name.trim() ||
                  !formData.description.trim() ||
                  createMoveMutation.isPending
                }
                className="bg-[#0033A0] hover:bg-[#002080]"
              >
                {createMoveMutation.isPending ? 'Creating...' : 'Create Move'}
              </Button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Name Field */}
            <div className="space-y-2">
              <Label htmlFor="name">
                Move Name <span className="text-destructive">*</span>
              </Label>
              <Input
                type="text"
                id="name"
                value={formData.name}
                onChange={e =>
                  setFormData({ ...formData, name: e.target.value })
                }
                placeholder="e.g., Armbar from Guard"
                maxLength={255}
                className={errors.name ? 'border-destructive' : ''}
              />
              {errors.name && (
                <p className="text-sm text-destructive">{errors.name}</p>
              )}
            </div>

            {/* Description Field */}
            <div className="space-y-2">
              <Label htmlFor="description">
                Description <span className="text-destructive">*</span>
              </Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={e =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows={4}
                placeholder="Describe the technique, setup, and key details..."
                className={errors.description ? 'border-destructive' : ''}
              />
              {errors.description && (
                <p className="text-sm text-destructive">{errors.description}</p>
              )}
            </div>

            {/* Video URL Field */}
            <div className="space-y-2">
              <Label htmlFor="url">Video URL</Label>
              <Input
                type="url"
                id="url"
                value={formData.url}
                onChange={e =>
                  setFormData({ ...formData, url: e.target.value })
                }
                placeholder="https://youtube.com/watch?v=example"
                maxLength={500}
                className={errors.url ? 'border-destructive' : ''}
              />
              <p className="text-xs text-muted-foreground">
                YouTube, Instagram, or other video platform URL
              </p>
              {errors.url && (
                <p className="text-sm text-destructive">{errors.url}</p>
              )}
            </div>

            {/* Thumbnail URL Field */}
            <div className="space-y-2">
              <Label htmlFor="thumbnailUrl">Thumbnail URL</Label>
              <Input
                type="url"
                id="thumbnailUrl"
                value={formData.thumbnailUrl}
                onChange={e =>
                  setFormData({ ...formData, thumbnailUrl: e.target.value })
                }
                placeholder="https://img.youtube.com/vi/example/maxresdefault.jpg"
                maxLength={500}
                className={errors.thumbnailUrl ? 'border-destructive' : ''}
              />
              <p className="text-xs text-muted-foreground">
                Optional: Direct link to thumbnail image
              </p>
              {errors.thumbnailUrl && (
                <p className="text-sm text-destructive">
                  {errors.thumbnailUrl}
                </p>
              )}
            </div>

            {/* Duration Field */}
            <div className="space-y-2">
              <Label htmlFor="durationSeconds">Duration (seconds)</Label>
              <Input
                type="number"
                id="durationSeconds"
                value={formData.durationSeconds}
                onChange={e =>
                  setFormData({ ...formData, durationSeconds: e.target.value })
                }
                placeholder="180"
                min="0"
                max="86400"
                className={errors.durationSeconds ? 'border-destructive' : ''}
              />
              <p className="text-xs text-muted-foreground">
                Video duration in seconds (optional)
              </p>
              {errors.durationSeconds && (
                <p className="text-sm text-destructive">
                  {errors.durationSeconds}
                </p>
              )}
            </div>
          </form>
        </Card>
      </main>
    </div>
  )
}
